import { Component, Input, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { StockOverviewDataService } from '../stock-screener/services/stock-overview-data-service.service';
import { ChangeDetectorRef } from '@angular/core';
import { SumPipe } from 'src/app/shared/pipes/sum.pipe';
import { IpoDataService } from 'src/app/shared/services/ipo-data.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { SubscriptionInfoModel } from 'src/app/models/subscription-info.model';

@Component({
    selector: 'app-ipo-data',
    templateUrl: './ipo-data.component.html',
    styleUrls: ['./ipo-data.component.scss'],
    standalone: false
})
export class IpoDataComponent implements OnInit {
   isSharePopupVisible = false;
   currentUrl = window.location.href;
   activeTab: number = 0;
   formattedUpdateDate: number = 0;
   reportDate: string = '';
   isHideShariahTab: boolean = false;
   showShariahTab: boolean = true;
   hideHistory: boolean = true;
   whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(
      this.currentUrl
   )}`;
   linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      this.currentUrl
   )}`;
   facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      this.currentUrl
   )}`;

   companyProfile: any = {}; // Now storing full profile object
   newDataStorage: any = {};
   currency: string | undefined;
   symbol: string | undefined;
   disclaimer: string | undefined;
   isSample: boolean = false;
   canView: boolean = true;
   type: string = 'ipo';
   type2: string = 'stock';
   screenerReportResponse: any;
   detailsLoader: boolean = false;
   public dataUnavailable: boolean = false;
   public isMobile = false;
   isUserLoggedIn: boolean = false;

   selectedTabIndex: number = 0; // For tab switching
   public reportTypeSection: any;
   public reportTypeYear: any;
   isAllComplianceAccessible: boolean = false;
   freeStocks: string[] = [];

   constructor(
      private route: ActivatedRoute,
      private observer: BreakpointObserver,
      private stockService: StockOverviewDataService,
      private changeDetectorRef: ChangeDetectorRef,
      private sumPipe: SumPipe,
      private ipoDataService: IpoDataService,
      private auth: AuthService
   ) {}

   ngOnInit(): void {
      this.isUserLoggedIn = !!this.auth.getUserId();
      SubscriptionService.subscriptionData.subscribe(async (data: SubscriptionInfoModel) => {
         if(data.isDataFetched) {
            if(data.isSubscribed) {
               this.isAllComplianceAccessible = true;
            } else {
               this.freeStocks = await this.stockService.getFreeIpoTickers();
            }
         }
      })
      this.getReport();
      // console.log("this.getReport()")
      // Get `symbol` from route parameter instead of query parameter
      this.route.paramMap.subscribe((params) => {
         const paramSymbol = params.get('symbol') || '';
         this.symbol = paramSymbol;
         if (this.symbol && this.symbol.trim() !== '') {
            // Make sure to call getNewData within this subscription block

            this.getNewData();
            this.getDates();
         } else {
            console.warn('No symbol found in route');
         }
      });

      // Mobile responsiveness observer
      this.observer.observe('(max-width: 1200px)').subscribe((result) => {
         this.isMobile = result.matches;
      });
   }

   // When screener data loads
   screenerDataLoaded(event: any) {
      this.screenerReportResponse = event;
   }

   // Tabs list
   getTabs() {
      return ['IPO Details', 'Shariah Compliance Report', 'Profile'];
   }

   // Get tab name by index
   getTabName(tabIndex: number) {
      const tabs = this.getTabs();
      return tabs[tabIndex] ?? '';
   }

   // When user clicks a tab
   selectTab(tabIndex: number) {
      this.selectedTabIndex = tabIndex;
   }

   // Check if tab is selected
   checkSelectedTab(tabIndex: number) {
      return this.selectedTabIndex === tabIndex;
   }

   toggleSharePopup() {
      this.isSharePopupVisible = !this.isSharePopupVisible;
   }

   copyLink() {
      navigator.clipboard
         .writeText(this.currentUrl)
         .then(() => alert('Link copied to clipboard!'))
         .catch((err) => alert('Failed to copy: ' + err));
   }

   @HostListener('document:click', ['$event'])
   handleOutsideClick(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const shareButton = target.closest('button'); // Check if click is on the share button
      const popup = target.closest(
         'div[style*="position: absolute; top: 110%; right: 0"]'
      ); // Check if click is inside popup

      // Close popup if click is outside both the button and the popup
      if (!shareButton && !popup && this.isSharePopupVisible) {
         this.isSharePopupVisible = false;
      }
   }

   onDetailedReportClicked() {
      this.selectedTabIndex = 1;
   }
   hasData(obj: any): boolean {
      return obj && Object.keys(obj).length > 0;
   }

   async getNewData() {
      if (!this.symbol) {
         console.error('Symbol is undefined in getNewData()');
         return;
      }

      this.detailsLoader = true;

      try {
         const res = await this.stockService.getSpecificIpo(this.symbol);
         this.newDataStorage = res;

         // Set showShariahTab based on sharia_compliance value
         this.showShariahTab =

            this.newDataStorage?.sharia_compliance !== 'NOT_UNDER_COVERAGE';
         //console.log("Shariah compliance status:", this.newDataStorage?.sharia_compliance, "Show tab:", this.showShariahTab);

         this.changeDetectorRef.detectChanges();
         this.detailsLoader = false;

         // Call getReport only if Shariah tab is visible
         if (this.showShariahTab) {
            this.getReport();
         }
      } catch (e) {
         console.error('Error fetching IPO data:', e);
         this.dataUnavailable = true;
         this.detailsLoader = false;
      }
   }

   async getDates() {
      if (!this.symbol) {
         console.error('Symbol is undefined in getNewData()');
         return;
      }

      try {
         const res = await this.stockService.getIpoDataByTicker(this.symbol);
         const rawDate = res.hits[0].document.updated_date;

         this.formattedUpdateDate = Math.floor(
            new Date(rawDate).getTime() / 1000
         );

         //console.log("Formatted date is:", this.formattedUpdateDate);

         this.changeDetectorRef.detectChanges();
      } catch (e) {
         console.error('Error fetching IPO data:', e);
         this.dataUnavailable = true;
         this.detailsLoader = false;
      }
   }
   async getReport() {
      const response = await this.stockService.getIpoComplianceReport(
         this.newDataStorage.company_symbol
      );
      // console.log("response",response)
      //console.log("shariah compliance data is ",response)
      this.reportTypeSection = response.reportTypeSection;
      this.reportTypeYear = response.reportTypeYear;
      this.reportDate = response.reportDate;
      //console.log("dates",this.reportTypeSection)
   }
   formatPriceRange(range: string): string {
      if (!range) return '-';

      try {
         const parts = range.split('-').map((part) => part.trim());

         if (parts.length === 1) {
            const num = parseFloat(parts[0]);
            return isNaN(num) ? range : num.toString();
         }

         if (parts.length === 2) {
            const firstNum = parseFloat(parts[0]);
            const secondNum = parseFloat(parts[1]);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
               return `${firstNum}-${secondNum}`;
            }
         }

         return range;
      } catch {
         return range;
      }
   }
   formatPriceWithSymbol(
      price: number | string,
      country: string = 'US'
   ): string {
      if (price === null || price === undefined || price === '') return '-';

      const symbol = country === 'IN' ? '₹' : '$';
      const num = typeof price === 'string' ? parseFloat(price) : price;

      if (isNaN(num)) return price.toString();

      return `${symbol}${num}`;
   }

   formatPriceRangeWithSymbol(range: string, country: string = 'US'): string {
      if (!range) return '-';

      const symbol = country === 'IN' ? '₹' : '$';

      try {
         const parts = range.split('-').map((part) => part.trim());

         if (parts.length === 1) {
            const num = parseFloat(parts[0]);
            return isNaN(num) ? range : `${symbol}${num}`;
         }

         if (parts.length === 2) {
            const firstNum = parseFloat(parts[0]);
            const secondNum = parseFloat(parts[1]);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
               return `${symbol}${firstNum}-${symbol}${secondNum}`;
            }
         }

         return range;
      } catch {
         return range;
      }
   }

   handleImageError(event: Event) {
      const imgElement = event.target as HTMLImageElement;
      imgElement.style.display = 'none';
      // The ngIf will automatically fall back to the initial template
   }
}
